/**
 * Notification Service Composable
 * 
 * The main composable for the VS Code-style notification system.
 * This is the ONLY way to create and manage notifications in the application.
 * 
 * Architecture:
 * - useNotifications (composable) - Main service (THIS FILE)
 * - ToastContainer (organism) - Displays notifications in App.vue
 * - ToastNotification (organism) - Individual notification wrapper
 * - NotificationContent (molecule) - Content layout with icon, message, actions
 * - NotificationMessage (atom) - Message text with truncation
 * - NotificationIcon (atom) - Severity-specific icons
 * - NotificationActionButton (atom) - Action buttons
 * 
 * Usage:
 * ```ts
 * import { useNotifications } from '@/composables/useNotifications';
 * 
 * const { success, error, warning, info } = useNotifications();
 * 
 * success('File saved successfully!', {
 *   source: 'editor.ts',
 *   actions: {
 *     primary: [
 *       { id: 'undo', label: 'Undo', onClick: () => undo() }
 *     ]
 *   }
 * });
 * ```
 */

import { ref, reactive, computed, nextTick } from 'vue';
import type { 
  ToastNotification, 
  NotificationOptions, 
  NotificationSeverity,
  NotificationMessage,
  NotificationStatus,
  NotificationHistoryConfig,
  NotificationHistoryFilter,
  NotificationHistoryStats,
  HttpResponseNotification 
} from '@/types/notifications';

// Single source of truth - all notifications live here
const notifications = ref<ToastNotification[]>([]);
const nextId = ref(1);

// Panel state
const panelOpen = ref(false);

// Computed: Active notifications (shown as toasts) - filter by visible flag
const activeNotifications = computed(() => 
  notifications.value.filter(n => n.visible === true)
);

// Default configuration
const defaultConfig = reactive({
  maxVisible: 5,
  defaultTimeout: 5000,
  position: 'top-right' as const,
  showProgress: true,
  enableStacking: true,
  spacing: 'normal' as const,
});

/**
 * Generate unique notification ID
 */
function generateId(): string {
  return `notification-${nextId.value++}`;
}

/**
 * Create notification message object
 */
function createMessage(text: string, links?: Array<{ text: string; href: string; title?: string; }>): NotificationMessage {
  return { text, links };
}

/**
 * Create a new toast notification
 */
function createNotification(
  message: string | NotificationMessage,
  severity: NotificationSeverity = 'info',
  options: Partial<NotificationOptions> = {}
): ToastNotification {
  const id = options.id || generateId();
  const notificationMessage: NotificationMessage = typeof message === 'string' ? { text: message } : message;
  
  // Auto-detect if message should be expandable based on length  // Auto-detect if message should be expandable based on length
  const shouldBeExpandable = options.expandable !== false && (
    notificationMessage.text.length > 80 || // Long text
    notificationMessage.text.includes('\n') || // Multi-line
    (options.actions?.primary && options.actions.primary.length > 2) || // Many actions
    !!options.source // Has source info
  );
  
  const notification: ToastNotification = {
    id,
    message: notificationMessage,
    severity,
    priority: options.priority || 'default',
    sticky: false, // No sticky notifications - all use timers
    timeout: options.timeout || defaultConfig.defaultTimeout,
    showOnlyOnce: options.showOnlyOnce || false,
    source: options.source,
    actions: options.actions,
    progress: options.progress,
    expandable: typeof options.expandable === 'boolean' ? options.expandable : shouldBeExpandable,
    expanded: options.expanded || false,
    visible: true,
    displayedInToast: false, // New notifications haven't been displayed yet
    createdAt: new Date(),
    originalCreatedAt: new Date(), // Store original creation time for display
    status: 'active',
    onShow: options.onShow,
    onHide: options.onHide,
    onClose: options.onClose,
    onAction: options.onAction,
  };
  
  return notification;
}

// Store for active timeout IDs and pause states
const activeTimeouts = new Map<string, number>();
const pausedTimeouts = new Map<string, { remainingTime: number; pausedAt: number }>();

/**
 * Setup automatic timeout for notification
 */
function setupNotificationTimeout(notification: ToastNotification): void {
  // Skip timer setup for notifications with timeout: 0 (like loading states)
  if (notification.timeout <= 0) {
    return;
  }
  
  // Clear existing timeout if any
  const existingTimeout = activeTimeouts.get(notification.id);
  if (existingTimeout) {
    clearTimeout(existingTimeout);
  }
  
  // Set new timeout
  const timeoutId = setTimeout(() => {
    const existingNotification = findNotification(notification.id);
    if (existingNotification) {
      existingNotification.visible = false; // Hide from toast, stays in history
      existingNotification.status = 'expired';
      existingNotification.expiredAt = new Date();
      existingNotification.onHide?.();
    }
    activeTimeouts.delete(notification.id);
  }, notification.timeout);
  
  activeTimeouts.set(notification.id, timeoutId);
}

/**
 * Pause notification timeout
 */
function pauseNotificationTimeout(id: string): void {
  const timeoutId = activeTimeouts.get(id);
  if (!timeoutId) return;
  
  const notification = findNotification(id);
  if (!notification) return;
  
  // Calculate remaining time
  const elapsed = Date.now() - notification.createdAt.getTime();
  const remainingTime = Math.max(0, notification.timeout - elapsed);
  
  // Store pause state
  pausedTimeouts.set(id, {
    remainingTime,
    pausedAt: Date.now()
  });
  
  // Clear the active timeout
  clearTimeout(timeoutId);
  activeTimeouts.delete(id);
}

/**
 * Resume notification timeout
 */
function resumeNotificationTimeout(id: string): void {
  const pausedState = pausedTimeouts.get(id);
  if (!pausedState) return;
  
  const notification = findNotification(id);
  if (!notification) return;
  
  // Calculate how long we were paused
  const pauseDuration = Date.now() - pausedState.pausedAt;
  
  // Adjust the notification's createdAt time to account for pause duration
  // This ensures getNotificationRemainingTime calculates correctly
  notification.createdAt = new Date(notification.createdAt.getTime() + pauseDuration);
  
  // Remove from paused state
  pausedTimeouts.delete(id);
  
  // Use the paused remaining time to ensure we don't dismiss immediately
  // even if there were calculation timing issues
  const timeoutDuration = Math.max(100, pausedState.remainingTime); // Minimum 100ms
  
  // Set new timeout with remaining time
  const timeoutId = setTimeout(() => {
    const existingNotification = findNotification(notification.id);
    if (existingNotification) {
      existingNotification.status = 'expired';
      existingNotification.expiredAt = new Date();
      existingNotification.visible = false; // Hide from toast but keep in list
      existingNotification.onHide?.();
    }
    activeTimeouts.delete(notification.id);
  }, timeoutDuration);
  
  activeTimeouts.set(id, timeoutId);
}

/**
 * Clear notification timeout
 */
function clearNotificationTimeout(id: string): void {
  const timeoutId = activeTimeouts.get(id);
  if (timeoutId) {
    clearTimeout(timeoutId);
    activeTimeouts.delete(id);
  }
  
  // Also clear any paused state
  pausedTimeouts.delete(id);
}

/**
 * Get remaining time for a notification
 */
function getNotificationRemainingTime(id: string): number {
  const notification = findNotification(id);
  if (!notification) return 0;
  
  // If sticky or no timeout, return 0
  if (notification.sticky || notification.timeout <= 0) {
    return 0;
  }
  
  // If paused, return the stored remaining time (frozen)
  const pausedState = pausedTimeouts.get(id);
  if (pausedState) {
    return pausedState.remainingTime;
  }
  
  // If active, calculate remaining time based on (possibly adjusted) createdAt
  const elapsed = Date.now() - notification.createdAt.getTime();
  return Math.max(0, notification.timeout - elapsed);
}

/**
 * Add notification to the stack
 */
function addNotification(notification: ToastNotification): string {
  notifications.value.push(notification);
  
  // Setup automatic timeout for all notifications
  setupNotificationTimeout(notification);
  
  // Call onShow callback
  nextTick(() => {
    notification.onShow?.();
  });
  
  return notification.id;
}

/**
 * Remove notification by ID
 */
function removeNotification(id: string): boolean {
  const index = notifications.value.findIndex((n: ToastNotification) => n.id === id);
  if (index === -1) return false;
  
  const notification = notifications.value[index];
  
  // Clear any active timeout
  clearNotificationTimeout(id);
  
  notification.onHide?.();
  
  // Remove completely from list (user manually cleared)
  notifications.value.splice(index, 1);
  return true;
}

/**
 * Find notification by ID
 */
function findNotification(id: string): ToastNotification | undefined {
  return notifications.value.find((n: ToastNotification) => n.id === id);
}

/**
 * Update notification properties
 */
function updateNotification(id: string, updates: Partial<ToastNotification>): boolean {
  const notification = findNotification(id);
  if (!notification) return false;
  
  Object.assign(notification, updates);
  return true;
}

/**
 * Clear all notifications
 */
function clearAll(): void {
  notifications.value.forEach((notification: ToastNotification) => {
    // Clear timeouts
    clearNotificationTimeout(notification.id);
    notification.onHide?.();
  });
  notifications.value.splice(0);
}

/**
 * Clear notifications by severity
 */
function clearBySeverity(severity: NotificationSeverity): void {
  const toRemove = notifications.value.filter((n: ToastNotification) => n.severity === severity);
  toRemove.forEach((notification: ToastNotification) => {
    notification.onHide?.();
    removeNotification(notification.id);
  });
}

/**
 * Main notification methods
 */
export function useNotifications() {
  // Computed values
  const hasNotifications = computed(() => notifications.value.length > 0);
  const errorCount = computed(() => notifications.value.filter((n: ToastNotification) => n.severity === 'error').length);
  const warningCount = computed(() => notifications.value.filter((n: ToastNotification) => n.severity === 'warning').length);
  
  // History computed values
  const historyStats = computed((): NotificationHistoryStats => {
    const stats = {
      total: notifications.value.length,
      active: 0,
      dismissed: 0,
      expired: 0,
      bySeverity: {
        info: 0,
        success: 0,
        warning: 0,
        error: 0,
      } as Record<NotificationSeverity, number>,
    };
    
    notifications.value.forEach((notification: ToastNotification) => {
      switch (notification.status) {
        case 'active':
          stats.active++;
          break;
        case 'dismissed':
          stats.dismissed++;
          break;
        case 'expired':
          stats.expired++;
          break;
      }
      
      stats.bySeverity[notification.severity]++;
    });
    
    return stats;
  });
  
  const activeNotificationCount = computed(() => activeNotifications.value.length);
  const historyNotificationCount = computed(() => notifications.value.length);
  
  // Quick notification methods
  const success = (message: string | NotificationMessage, options?: Partial<NotificationOptions>) => {
    const notification = createNotification(message, 'success', options);
    return addNotification(notification);
  };
  
  const info = (message: string | NotificationMessage, options?: Partial<NotificationOptions>) => {
    const notification = createNotification(message, 'info', options);
    return addNotification(notification);
  };
  
  const warning = (message: string | NotificationMessage, options?: Partial<NotificationOptions>) => {
    const notification = createNotification(message, 'warning', {
      ...options,
    });
    return addNotification(notification);
  };
  
  const error = (message: string | NotificationMessage, options?: Partial<NotificationOptions>) => {
    const notification = createNotification(message, 'error', {
      ...options,
    });
    return addNotification(notification);
  };
  
  /**
   * Handle HTTP response notifications
   */
  const handleHttpResponse = (response: HttpResponseNotification, options?: Partial<NotificationOptions>) => {
    const { status, statusText, message: responseMessage, details } = response;
    
    let severity: NotificationSeverity;
    let message: string;
    
    // Determine severity based on status code
    if (status >= 200 && status < 300) {
      severity = 'success';
      message = responseMessage || 'Request successful';
    } else if (status >= 400 && status < 500) {
      severity = 'error';
      message = responseMessage || `${status} ${statusText || 'Client Error'}`;
    } else if (status >= 500) {
      severity = 'error';
      message = responseMessage || `Server error: ${status} ${statusText || 'Internal Server Error'}`;
    } else {
      severity = 'info';
      message = responseMessage || `${status} ${statusText || ''}`;
    }
    
    const notificationOptions: Partial<NotificationOptions> = {
      ...options,
      source: { id: 'http', label: `HTTP ${status}` },
      expandable: !!details,
    };
    
    return createNotification(message, severity, notificationOptions);
  };
  
  /**
   * History management functions
   */
  const getFilteredHistory = (filter?: NotificationHistoryFilter) => {
    let filtered = [...notifications.value];
    
    if (!filter) return filtered;
    
    if (filter.severity?.length) {
      filtered = filtered.filter(n => filter.severity!.includes(n.severity));
    }
    
    if (filter.status?.length) {
      filtered = filtered.filter(n => filter.status!.includes(n.status));
    }
    
    if (filter.dateRange) {
      filtered = filtered.filter(n => 
        n.createdAt >= filter.dateRange!.start && 
        n.createdAt <= filter.dateRange!.end
      );
    }
    
    if (filter.searchQuery) {
      const query = filter.searchQuery.toLowerCase();
      filtered = filtered.filter(n => 
        n.message.text.toLowerCase().includes(query) ||
        (typeof n.source === 'string' && n.source.toLowerCase().includes(query)) ||
        (typeof n.source === 'object' && n.source.label.toLowerCase().includes(query))
      );
    }
    
    if (filter.source) {
      filtered = filtered.filter(n => {
        if (typeof n.source === 'string') return n.source === filter.source;
        if (typeof n.source === 'object') return n.source.id === filter.source;
        return false;
      });
    }
    
    return filtered;
  };
  
  const clearHistory = () => {
    notifications.value.splice(0);
  };
  
  const clearHistoryByStatus = (status: NotificationStatus) => {
    notifications.value = notifications.value.filter((n: ToastNotification) => n.status !== status);
  };
  
  const removeFromHistory = (id: string) => {
    const index = notifications.value.findIndex((n: ToastNotification) => n.id === id);
    if (index >= 0) {
      notifications.value.splice(index, 1);
    }
  };
  
  /**
   * Notification event handlers
   */
  const handleExpand = (id: string) => {
    // Just update the notification in place - no dual list management
    updateNotification(id, { expanded: true });
  };
  
  const handleCollapse = (id: string) => {
    // Just update the notification in place - no dual list management
    updateNotification(id, { expanded: false });
  };
  
  const handleClose = (id: string) => {
    const notification = findNotification(id);
    if (notification) {
      // Skip if already closed
      if (!notification.visible) {
        return;
      }
      
      notification.onClose?.();
      // Just hide from toast - notification stays in history
      notification.visible = false;
      notification.status = 'dismissed';
      notification.dismissedAt = new Date();
      clearNotificationTimeout(id);
    }
  };
  
  const handleAction = (id: string, actionId: string) => {
    const notification = findNotification(id);
    if (!notification) return;
    
    // Find and execute action
    const primaryAction = notification.actions?.primary?.find(a => a.id === actionId);
    const secondaryAction = notification.actions?.secondary?.find(a => a.id === actionId);
    
    const action = primaryAction || secondaryAction;
    if (action) {
      action.onClick();
      notification.onAction?.(actionId);
      
      // Close notification unless action specifies to keep open
      if (!action.keepOpen) {
        handleClose(id);
      }
    }
  };
  
  const handleRemoved = (_id: string) => {
    // Do nothing - notifications persist in history until manually cleared
    // This event fires when toast animation completes, but we want to keep
    // the notification in the array for history display
  };

  /**
   * Mark notification as having been displayed in toast stack
   */
  const markAsDisplayedInToast = (id: string) => {
    const notification = findNotification(id);
    if (notification) {
      notification.displayedInToast = true;
    }
  };

  /**
   * Close notification from history panel
   */
  const closeFromHistory = (id: string) => {
    // Remove from history
    removeFromHistory(id);
    
    // Also remove from active notifications if still present
    const activeNotification = findNotification(id);
    if (activeNotification) {
      removeNotification(id);
    }
  };
  
  /**
   * Panel state management
   */
  const openPanel = () => {
    panelOpen.value = true;
  };
  
  const closePanel = () => {
    panelOpen.value = false;
  };
  
  /**
   * Configuration methods
   */
  const configure = (config: Partial<typeof defaultConfig>) => {
    Object.assign(defaultConfig, config);
  };
  
  const getConfig = () => ({ ...defaultConfig });
  
  return {
    // State - single source of truth
    notifications,  // Export the ref directly
    activeNotifications,  // Computed filter for active toasts
    hasNotifications,
    errorCount,
    warningCount,
    config: computed(() => defaultConfig),
    panelOpen,
    
    // History state
    historyStats,
    activeNotificationCount,
    historyNotificationCount,
    
    // Quick methods
    success,
    info,
    warning,
    error,
    
    // Advanced methods
    notify: (message: string | NotificationMessage, severity: NotificationSeverity, options?: Partial<NotificationOptions>) => {
      const notification = createNotification(message, severity, options);
      return addNotification(notification);
    },
    handleHttpResponse,
    
    // Management methods
    remove: removeNotification,
    update: updateNotification,
    find: findNotification,
    clear: clearAll,
    clearBySeverity,
    
    // Timer management
    pauseNotificationTimeout,
    resumeNotificationTimeout,
    getNotificationRemainingTime,
    
    // History management
    getFilteredHistory,
    clearHistory,
    clearHistoryByStatus,
    removeFromHistory,
    
    // Event handlers (for use with ToastContainer)
    handleExpand,
    handleCollapse,
    handleClose,
    handleAction,
    handleRemoved,
    markAsDisplayedInToast,
    closeFromHistory,
    
    // Panel management
    openPanel,
    closePanel,
    
    // Configuration
    configure,
    getConfig,
    
    // Utilities
    createMessage,
  };
}

// Export singleton instance for global use
export const notificationService = useNotifications();

// Export types for consumers
export type { 
  NotificationOptions, 
  ToastNotification, 
  NotificationSeverity, 
  NotificationStatus,
  NotificationHistoryConfig,
  NotificationHistoryFilter,
  NotificationHistoryStats
};
